import React, { useEffect, useMemo, useState } from "react";
import HomeLayout from "../../asset/layout/HomeLayout";
import { getAllBudgetreeLeads } from "../../actions/userAuth";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Stack,
  Grid,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import CustomButton from "../../components/mui/CustomButton";
import ResponsiveDatePickers from "../../components/mui/PichDate";
import SearchBar from "../../components/mui/SearchBar";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { format } from "date-fns";
import { enqueueSnackbar } from "notistack";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";

const EMPLOYEE_BANDS = [
  { id: "all", label: "All team sizes" },
  { id: "1-10", label: "1–10 employees", min: 1, max: 10 },
  { id: "11-50", label: "11–50 employees", min: 11, max: 50 },
  { id: "51-200", label: "51–200 employees", min: 51, max: 200 },
  { id: "201+", label: "201+ employees", min: 201, max: Infinity },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: "24px",
      border: "1px solid",
      borderColor: alpha("#11409B", 0.08),
      bgcolor: "#fff",
      boxShadow: "0 4px 32px rgba(15, 23, 42, 0.06)",
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Card>
);

const StatCard = ({ icon: Icon, label, value, accent, gradient }) => (
  <PanelCard sx={{ height: "100%" }}>
    <Box sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 8px 24px ${alpha(accent, 0.35)}`,
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "#64748b",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontSize: "0.7rem",
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.75rem",
              color: "#0f172a",
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
            }}
          >
            {value ?? "—"}
          </Typography>
        </Box>
      </Stack>
    </Box>
  </PanelCard>
);

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: "#fff",
    "& fieldset": { borderColor: alpha("#11409B", 0.2) },
    "&:hover fieldset": { borderColor: "#11409B" },
  },
};

const display = (val) => (val != null && val !== "" ? val : "—");

const parseEmployeeCount = (val) => {
  const n = Number(String(val || "").replace(/[^\d]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const getEmployeeBand = (count) => {
  const n = parseEmployeeCount(count);
  if (n == null) return null;
  if (n <= 10) return { label: "1–10", color: "#0891b2" };
  if (n <= 50) return { label: "11–50", color: "#2563eb" };
  if (n <= 200) return { label: "51–200", color: "#7c3aed" };
  return { label: "201+", color: "#059669" };
};

const getLeadName = (row) => {
  const parts = [row?.firstName, row?.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : row?.email || "—";
};

const getInitials = (row) => getLeadName(row).charAt(0).toUpperCase();

const getLeadDate = (row) => row?.createdAt || row?.updatedAt;

const matchesEmployeeBand = (row, bandId) => {
  if (!bandId || bandId === "all") return true;
  const band = EMPLOYEE_BANDS.find((b) => b.id === bandId);
  const n = parseEmployeeCount(row?.employeeCount);
  if (!band || n == null) return bandId === "all";
  return n >= band.min && n <= band.max;
};

const BudgetreeLeads = () => {
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDateFrom, setSelectedDateFrom] = useState(null);
  const [selectedDateTo, setSelectedDateTo] = useState(null);
  const [applicationFilter, setApplicationFilter] = useState("");
  const [employeeBand, setEmployeeBand] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/");
    }
  }, [navigate]);

  const getData = () => {
    setLoading(true);
    dispatch(
      getAllBudgetreeLeads(
        (data) => {
          setUserData(Array.isArray(data?.data?.data) ? data.data.data : []);
          setLoading(false);
        },
        () => setLoading(false)
      )
    );
  };

  useEffect(() => {
    getData();
  }, [dispatch]);

  const applicationOptions = useMemo(() => {
    const set = new Set();
    userData.forEach((row) => {
      if (row?.useOfBudgetree) set.add(row.useOfBudgetree);
    });
    return Array.from(set).sort();
  }, [userData]);

  const filteredData = useMemo(() => {
    let rows = [...userData];

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter((item) =>
        [
          item?.firstName,
          item?.lastName,
          item?.email,
          item?.phoneNumber,
          item?.useOfBudgetree,
          item?.employeeCount?.toString(),
        ].some((field) => field?.toLowerCase?.().includes(q))
      );
    }

    if (applicationFilter) {
      rows = rows.filter((r) => r?.useOfBudgetree === applicationFilter);
    }

    rows = rows.filter((r) => matchesEmployeeBand(r, employeeBand));

    if (selectedDateFrom || selectedDateTo) {
      rows = rows.filter((item) => {
        const createdAt = new Date(getLeadDate(item));
        if (Number.isNaN(createdAt.getTime())) return false;
        const afterStart =
          !selectedDateFrom || createdAt >= new Date(selectedDateFrom);
        const beforeEnd = !selectedDateTo || (() => {
          const end = new Date(selectedDateTo);
          end.setHours(23, 59, 59, 999);
          return createdAt <= end;
        })();
        return afterStart && beforeEnd;
      });
    }

    rows.sort((a, b) => {
      const da = new Date(getLeadDate(a)).getTime() || 0;
      const db = new Date(getLeadDate(b)).getTime() || 0;
      return sortOrder === "oldest" ? da - db : db - da;
    });

    return rows;
  }, [
    userData,
    searchTerm,
    applicationFilter,
    employeeBand,
    selectedDateFrom,
    selectedDateTo,
    sortOrder,
  ]);

  const stats = useMemo(() => {
    const total = filteredData.length;
    const thisMonth = filteredData.filter((l) => {
      const d = getLeadDate(l);
      return d ? dayjs(d).isSame(dayjs(), "month") : false;
    }).length;
    const applications = new Set(
      filteredData.map((l) => l?.useOfBudgetree).filter(Boolean)
    ).size;
    const enterprise = filteredData.filter((l) => {
      const n = parseEmployeeCount(l?.employeeCount);
      return n != null && n >= 51;
    }).length;
    const avgEmployees = (() => {
      const counts = filteredData
        .map((l) => parseEmployeeCount(l?.employeeCount))
        .filter((n) => n != null);
      if (!counts.length) return "—";
      return Math.round(counts.reduce((a, b) => a + b, 0) / counts.length);
    })();
    return { total, thisMonth, applications, enterprise, avgEmployees };
  }, [filteredData]);

  const totalPages = Math.ceil(filteredData.length / countPerPage) || 1;
  const paginatedData = filteredData.slice(
    (currentPage - 1) * countPerPage,
    currentPage * countPerPage
  );

  const resetPage = () => setCurrentPage(1);

  const handleSearch = (term) => {
    setSearchTerm(term);
    resetPage();
  };

  const handleSearchClear = () => {
    setSearchTerm("");
    setSelectedDateFrom(null);
    setSelectedDateTo(null);
    setApplicationFilter("");
    setEmployeeBand("all");
    setSortOrder("newest");
    resetPage();
  };

  const formatDateIndian = (dateString) => {
    const date = new Date(dateString);
    return format(date, "dd/MM/yyyy", { timeZone: "Asia/Kolkata" });
  };

  const copyToClipboard = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(String(text));
      enqueueSnackbar(`${label} copied`, { variant: "success" });
    } catch {
      enqueueSnackbar("Could not copy to clipboard", { variant: "error" });
    }
  };

  const openDetail = (lead) => {
    setSelectedLead(lead);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedLead(null);
  };

  const exportToExcel = () => {
    const header = [
      "First Name",
      "Last Name",
      "Email",
      "Phone Number",
      "Application",
      "No. of Employees",
      "Created On",
    ];
    const rows = filteredData.map((user) => [
      user?.firstName,
      user?.lastName,
      user?.email,
      user?.phoneNumber,
      user?.useOfBudgetree,
      user?.employeeCount,
      getLeadDate(user) ? formatDateIndian(getLeadDate(user)) : "—",
    ]);
    rows.unshift(header);
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Budgetree Leads");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([excelBuffer], { type: "application/octet-stream" }),
      "Budgetree_Leads.xlsx"
    );
  };

  const premiumButtonSx = {
    height: "44px",
    borderRadius: "12px",
    fontWeight: 600,
    textTransform: "none",
    background: "linear-gradient(135deg, #11409B 0%, #2563eb 100%)",
    boxShadow: "0 6px 24px rgba(37, 99, 235, 0.35)",
    "&:hover": {
      background: "linear-gradient(135deg, #0f3580 0%, #1d4ed8 100%)",
    },
  };

  const activeFilterCount = [
    searchTerm,
    selectedDateFrom,
    selectedDateTo,
    applicationFilter,
    employeeBand !== "all" ? employeeBand : "",
    sortOrder !== "newest" ? sortOrder : "",
  ].filter(Boolean).length;

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: "100%",
          background:
            "linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)",
          mx: -3,
          mt: -3,
          px: 3,
          pt: 3,
          pb: 4,
        }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: "24px",
              background:
                "linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(17, 64, 155, 0.35)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -30,
                width: 200,
                height: 200,
                borderRadius: "50%",
                bgcolor: alpha("#fff", 0.06),
              }}
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <HubOutlinedIcon />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, letterSpacing: "0.1em", opacity: 0.85 }}
                  >
                    BUDGETREE
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, letterSpacing: "-0.03em" }}
                >
                  Budgetree leads
                </Typography>
                <Typography sx={{ mt: 1, opacity: 0.82, maxWidth: 560 }}>
                  Inbound sign-ups with application intent and team size. Filter,
                  inspect details, copy contact info, or export your shortlist.
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Tooltip title="Refresh list">
                  <span>
                    <CustomButton
                      variant="contained"
                      onClick={getData}
                      disabled={loading}
                      sx={{
                        ...premiumButtonSx,
                        minWidth: 48,
                        px: 1.5,
                        bgcolor: alpha("#fff", 0.15),
                        backdropFilter: "blur(8px)",
                        border: `1px solid ${alpha("#fff", 0.25)}`,
                        boxShadow: "none",
                        "&:hover": { bgcolor: alpha("#fff", 0.22) },
                      }}
                    >
                      <RefreshOutlinedIcon />
                    </CustomButton>
                  </span>
                </Tooltip>
                <CustomButton
                  variant="contained"
                  onClick={exportToExcel}
                  startIcon={<FileDownloadOutlinedIcon />}
                  sx={{
                    ...premiumButtonSx,
                    bgcolor: alpha("#fff", 0.15),
                    backdropFilter: "blur(8px)",
                    border: `1px solid ${alpha("#fff", 0.25)}`,
                    boxShadow: "none",
                    "&:hover": { bgcolor: alpha("#fff", 0.22) },
                  }}
                >
                  Export ({filteredData.length})
                </CustomButton>
              </Stack>
            </Stack>
          </Box>
        </motion.div>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {[
            {
              icon: GroupsOutlinedIcon,
              label: "Total leads",
              value: stats.total,
              accent: "#4f46e5",
              gradient: "linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)",
            },
            {
              icon: TrendingUpOutlinedIcon,
              label: "This month",
              value: stats.thisMonth,
              accent: "#059669",
              gradient: "linear-gradient(135deg, #34d399 0%, #059669 100%)",
            },
            {
              icon: AppsOutlinedIcon,
              label: "Use cases",
              value: stats.applications,
              accent: "#2563eb",
              gradient: "linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)",
            },
            {
              icon: BusinessCenterOutlinedIcon,
              label: "Avg team size",
              value: stats.avgEmployees,
              accent: "#7c3aed",
              gradient: "linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)",
            },
          ].map((stat, i) => (
            <Grid item xs={12} sm={6} md={3} key={stat.label}>
              <motion.div custom={i} initial="hidden" animate="visible" variants={fadeUp}>
                <StatCard {...stat} />
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1}
              mb={2}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <FilterListOutlinedIcon sx={{ color: "#11409B", fontSize: 20 }} />
                <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                  Filters
                </Typography>
                {activeFilterCount > 0 && (
                  <Chip
                    label={`${activeFilterCount} active`}
                    size="small"
                    sx={{
                      bgcolor: alpha("#11409B", 0.1),
                      color: "#11409B",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                    }}
                  />
                )}
              </Stack>
              <Chip
                label={`${stats.enterprise} leads with 51+ employees`}
                size="small"
                sx={{
                  bgcolor: alpha("#059669", 0.1),
                  color: "#059669",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
            </Stack>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <SearchBar
                  onSearch={handleSearch}
                  searchTerm={searchTerm}
                  height="2.75rem"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateFrom}
                  setSelectedDate={(d) => {
                    setSelectedDateFrom(d);
                    resetPage();
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateTo}
                  setSelectedDate={(d) => {
                    setSelectedDateTo(d);
                    resetPage();
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Autocomplete
                  options={applicationOptions}
                  value={applicationFilter || null}
                  onChange={(_, val) => {
                    setApplicationFilter(val || "");
                    resetPage();
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Application"
                      placeholder="All use cases"
                      sx={fieldSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth sx={fieldSx}>
                  <InputLabel>Team size</InputLabel>
                  <Select
                    value={employeeBand}
                    label="Team size"
                    onChange={(e) => {
                      setEmployeeBand(e.target.value);
                      resetPage();
                    }}
                  >
                    {EMPLOYEE_BANDS.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth sx={fieldSx}>
                  <InputLabel>Sort</InputLabel>
                  <Select
                    value={sortOrder}
                    label="Sort"
                    onChange={(e) => setSortOrder(e.target.value)}
                  >
                    <MenuItem value="newest">Newest first</MenuItem>
                    <MenuItem value="oldest">Oldest first</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomButton
                  variant="outlined"
                  onClick={handleSearchClear}
                  fullWidth
                  sx={{
                    height: "2.75rem",
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: alpha("#11409B", 0.25),
                    color: "#11409B",
                    "&:hover": {
                      borderColor: "#11409B",
                      bgcolor: alpha("#11409B", 0.04),
                    },
                  }}
                >
                  Clear all
                </CustomButton>
              </Grid>
            </Grid>
          </PanelCard>
        </motion.div>

        <motion.div custom={5} initial="hidden" animate="visible" variants={fadeUp}>
          {loading ? (
            <PanelCard>
              <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
                <CircularProgress sx={{ color: "#11409B" }} />
                <Typography sx={{ mt: 2, color: "#64748b" }}>
                  Loading Budgetree leads…
                </Typography>
              </Stack>
            </PanelCard>
          ) : paginatedData.length > 0 ? (
            <TableContainer component={PanelCard} sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha("#11409B", 0.04),
                      "& th": {
                        borderBottom: `1px solid ${alpha("#11409B", 0.08)}`,
                      },
                    }}
                  >
                    {[
                      "Lead",
                      "Email",
                      "Phone",
                      "Application",
                      "Team size",
                      "Created",
                      "Actions",
                    ].map((column) => (
                      <TableCell
                        key={column}
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: "#11409B",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          py: 2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row) => {
                    const band = getEmployeeBand(row?.employeeCount);
                    return (
                      <TableRow
                        key={row._id || row.id || row.email}
                        hover
                        onClick={() => openDetail(row)}
                        sx={{
                          cursor: "pointer",
                          transition: "background 0.2s",
                          "&:hover": { bgcolor: alpha("#11409B", 0.03) },
                          "& td": {
                            borderBottom: `1px solid ${alpha("#11409B", 0.06)}`,
                            py: 1.75,
                          },
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar
                              sx={{
                                width: 44,
                                height: 44,
                                background:
                                  "linear-gradient(135deg, #11409B, #2563eb)",
                                fontWeight: 700,
                                border: "2px solid #fff",
                                boxShadow: "0 4px 12px rgba(17,64,155,0.2)",
                              }}
                            >
                              {getInitials(row)}
                            </Avatar>
                            <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                              {getLeadName(row)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "#475569" }}>
                            {display(row?.email)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "#475569" }}>
                            {row?.phoneNumber ? `+91 ${row.phoneNumber}` : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row?.useOfBudgetree ? (
                            <Chip
                              label={row.useOfBudgetree}
                              size="small"
                              sx={{
                                bgcolor: alpha("#11409B", 0.08),
                                color: "#11409B",
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                maxWidth: 160,
                              }}
                            />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography sx={{ fontWeight: 700, color: "#0d9488" }}>
                              {display(row?.employeeCount)}
                            </Typography>
                            {band && (
                              <Chip
                                label={band.label}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: "0.65rem",
                                  fontWeight: 700,
                                  bgcolor: alpha(band.color, 0.12),
                                  color: band.color,
                                }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "#64748b" }}>
                            {getLeadDate(row)
                              ? dayjs(getLeadDate(row)).format("DD MMM YYYY")
                              : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="View details">
                              <IconButton
                                size="small"
                                onClick={() => openDetail(row)}
                                sx={{
                                  borderRadius: "10px",
                                  border: `1px solid ${alpha("#11409B", 0.15)}`,
                                  color: "#11409B",
                                }}
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {row?.email && (
                              <Tooltip title="Copy email">
                                <IconButton
                                  size="small"
                                  onClick={() => copyToClipboard(row.email, "Email")}
                                  sx={{
                                    borderRadius: "10px",
                                    border: `1px solid ${alpha("#11409B", 0.15)}`,
                                  }}
                                >
                                  <EmailOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <PanelCard>
              <Stack alignItems="center" sx={{ py: 8, px: 3 }}>
                <HubOutlinedIcon sx={{ fontSize: 56, color: "#cbd5e1", mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  No leads found
                </Typography>
                <Typography sx={{ color: "#64748b", mt: 1, textAlign: "center" }}>
                  Try adjusting filters or refresh to load the latest sign-ups.
                </Typography>
                <CustomButton
                  variant="contained"
                  onClick={handleSearchClear}
                  sx={{ ...premiumButtonSx, mt: 3 }}
                >
                  Clear filters
                </CustomButton>
              </Stack>
            </PanelCard>
          )}
        </motion.div>

        {!loading && filteredData.length > 0 && (
          <motion.div custom={6} initial="hidden" animate="visible" variants={fadeUp}>
            <PanelCard sx={{ mt: 3, p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography sx={{ color: "#64748b", fontWeight: 500 }}>
                  Showing {(currentPage - 1) * countPerPage + 1}–
                  {Math.min(currentPage * countPerPage, filteredData.length)} of{" "}
                  {filteredData.length}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Rows per page
                  </Typography>
                  <Box
                    component="select"
                    value={countPerPage}
                    onChange={(e) => {
                      setCountPerPage(Number(e.target.value));
                      resetPage();
                    }}
                    sx={{
                      border: `1px solid ${alpha("#11409B", 0.2)}`,
                      borderRadius: "10px",
                      px: 1.5,
                      py: 0.75,
                      fontSize: "0.875rem",
                      color: "#0f172a",
                      bgcolor: "#fff",
                      outline: "none",
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      sx={{
                        border: "1px solid",
                        borderColor: alpha("#11409B", 0.15),
                        borderRadius: "12px",
                        bgcolor: "#fff",
                      }}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <Typography sx={{ fontWeight: 600, minWidth: 48, textAlign: "center" }}>
                      {currentPage} / {totalPages}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        setCurrentPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={currentPage >= totalPages}
                      sx={{
                        border: "1px solid",
                        borderColor: alpha("#11409B", 0.15),
                        borderRadius: "12px",
                        bgcolor: "#fff",
                      }}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </PanelCard>
          </motion.div>
        )}
      </Box>

      <Modal open={detailOpen} onClose={closeDetail}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { md: 480, xs: "92%" },
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "#fff",
            borderRadius: "24px",
            boxShadow: "0 24px 80px rgba(15, 23, 42, 0.2)",
            outline: "none",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 3,
              py: 2,
              borderBottom: `1px solid ${alpha("#11409B", 0.08)}`,
            }}
          >
            <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
              Lead details
            </Typography>
            <IconButton onClick={closeDetail} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          {selectedLead && (
            <Box sx={{ p: 3 }}>
              <Stack alignItems="center" spacing={2} mb={3}>
                <Avatar
                  sx={{
                    width: 88,
                    height: 88,
                    background: "linear-gradient(135deg, #11409B, #2563eb)",
                    fontWeight: 700,
                    fontSize: "1.75rem",
                    boxShadow: "0 8px 24px rgba(17,64,155,0.25)",
                  }}
                >
                  {getInitials(selectedLead)}
                </Avatar>
                <Box textAlign="center">
                  <Typography sx={{ fontWeight: 800, fontSize: "1.25rem" }}>
                    {getLeadName(selectedLead)}
                  </Typography>
                  {selectedLead?.useOfBudgetree && (
                    <Chip
                      label={selectedLead.useOfBudgetree}
                      sx={{
                        mt: 1,
                        bgcolor: alpha("#11409B", 0.1),
                        color: "#11409B",
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
              </Stack>

              <Stack spacing={1.5}>
                {[
                  {
                    icon: EmailOutlinedIcon,
                    label: "Email",
                    value: selectedLead?.email,
                    copy: selectedLead?.email,
                  },
                  {
                    icon: PhoneOutlinedIcon,
                    label: "Phone",
                    value: selectedLead?.phoneNumber
                      ? `+91 ${selectedLead.phoneNumber}`
                      : null,
                    copy: selectedLead?.phoneNumber,
                  },
                  {
                    icon: AppsOutlinedIcon,
                    label: "Application / use case",
                    value: selectedLead?.useOfBudgetree,
                  },
                  {
                    icon: GroupsOutlinedIcon,
                    label: "Team size",
                    value: selectedLead?.employeeCount,
                  },
                  {
                    icon: HubOutlinedIcon,
                    label: "Submitted on",
                    value: getLeadDate(selectedLead)
                      ? dayjs(getLeadDate(selectedLead)).format(
                          "DD MMM YYYY, hh:mm A"
                        )
                      : null,
                  },
                ].map(({ icon: Icon, label, value, copy }) => (
                  <Box
                    key={label}
                    sx={{
                      p: 1.5,
                      borderRadius: "12px",
                      bgcolor: alpha("#11409B", 0.04),
                      border: `1px solid ${alpha("#11409B", 0.06)}`,
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Icon sx={{ fontSize: 18, color: "#64748b" }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                            {label}
                          </Typography>
                          <Typography sx={{ fontWeight: 600, color: "#0f172a" }}>
                            {display(value)}
                          </Typography>
                        </Box>
                      </Stack>
                      {copy && (
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(copy, label)}
                        >
                          <ContentCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Modal>
    </HomeLayout>
  );
};

export default BudgetreeLeads;
